// YouTubeUpload.js
// YouTube Video to Learning Goal Component
// Extracts video info and generates learning roadmap from YouTube videos

import React, { useState } from 'react';
import axios from 'axios';
import { X, Youtube, Loader, CheckCircle, AlertTriangle } from 'lucide-react';

const YouTubeUpload = ({ onGenerateRoadmap, onClose, darkMode, makeOpenAIRequest, onStudyCompanion }) => {
  const [videoUrl, setVideoUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [videoInfo, setVideoInfo] = useState(null);
  const [step, setStep] = useState('input'); // 'input', 'preview', 'generating', 'mode-select'
  const [selectedMode, setSelectedMode] = useState(null); // 'roadmap', 'flashcards', 'companion'

  const YOUTUBE_API_KEY = 'AIzaSyDvLu6R_G9HE2y7fxRRxI8q6beK_BTVuTI';

  // Extract video ID from YouTube URL
  const extractVideoId = (url) => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /^([a-zA-Z0-9_-]{11})$/ // Direct video ID
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    return null;
  };

  // Fetch video information from YouTube API
  const fetchVideoInfo = async () => {
    setLoading(true);
    setError(null);

    try {
      const videoId = extractVideoId(videoUrl);

      if (!videoId) {
        throw new Error('Invalid YouTube URL. Please enter a valid YouTube video link.');
      }

      // Fetch video details
      const videoResponse = await axios.get(
        `https://www.googleapis.com/youtube/v3/videos?part=snippet,contentDetails,statistics&id=${videoId}&key=${YOUTUBE_API_KEY}`
      );

      if (!videoResponse.data.items || videoResponse.data.items.length === 0) {
        throw new Error('Video not found. Please check the URL and try again.');
      }

      const video = videoResponse.data.items[0];

      // Try to fetch captions/transcript
      let transcript = null;
      try {
        const captionsResponse = await axios.get(
          `https://www.googleapis.com/youtube/v3/captions?part=snippet&videoId=${videoId}&key=${YOUTUBE_API_KEY}`
        );

        if (captionsResponse.data.items && captionsResponse.data.items.length > 0) {
          transcript = 'Transcript available';
        }
      } catch (captionError) {
        console.log('Captions not available:', captionError);
      }

      const info = {
        id: videoId,
        title: video.snippet.title,
        description: video.snippet.description,
        channelTitle: video.snippet.channelTitle,
        duration: video.contentDetails.duration,
        thumbnail: video.snippet.thumbnails.high.url,
        viewCount: video.statistics.viewCount,
        publishedAt: video.snippet.publishedAt,
        tags: video.snippet.tags || [],
        transcript: transcript
      };

      setVideoInfo(info);
      setStep('mode-select'); // Show mode selection instead of preview
      setLoading(false);
    } catch (err) {
      console.error('Error fetching video info:', err);
      setError(err.message || 'Failed to fetch video information. Please try again.');
      setLoading(false);
    }
  };

  // Generate learning roadmap from video content
  const generateLearningRoadmap = async () => {
    setStep('generating');
    setLoading(true);
    setError(null);

    try {
      // Create a comprehensive prompt from video information
      const videoContent = `
        Video Title: ${videoInfo.title}
        Channel: ${videoInfo.channelTitle}
        Description: ${videoInfo.description}
        Tags: ${videoInfo.tags.join(', ')}
      `.trim();

      // Generate roadmap using AI
      const messages = [
        {
          role: 'system',
          content: `You are an expert educational curriculum designer. Based on the YouTube video information provided, create a comprehensive learning roadmap. The roadmap should break down the video content into structured lessons that help students master the topic.

Return a JSON object with this EXACT structure:
{
  "title": "Learning Goal Title based on video content",
  "description": "Brief description of what students will learn",
  "duration": "Estimated time (e.g., 2 weeks or 1 month)",
  "lessons": [
    {
      "day": 1,
      "title": "Lesson Title",
      "description": "What this lesson covers",
      "content": "Detailed lesson content and key concepts",
      "tasks": ["Task 1", "Task 2", "Task 3"],
      "duration": "30-45 mins",
      "resources": [
        {
          "title": "Original YouTube Video",
          "url": "https://youtube.com/watch?v=VIDEO_ID",
          "type": "video"
        }
      ]
    }
  ]
}`
        },
        {
          role: 'user',
          content: `Create a structured learning roadmap (5-8 lessons) based on this YouTube video:

${videoContent}

Make the lessons progressive, starting from fundamentals and building to advanced concepts. Include practical tasks and reference the original video as a resource.`
        }
      ];

      const response = await makeOpenAIRequest(messages, 3000);
      const roadmapData = JSON.parse(response);

      // Add video reference to roadmap
      roadmapData.videoSource = {
        id: videoInfo.id,
        title: videoInfo.title,
        url: `https://www.youtube.com/watch?v=${videoInfo.id}`,
        thumbnail: videoInfo.thumbnail
      };

      // Generate flashcards alongside the roadmap
      const flashcardMessages = [
        {
          role: 'system',
          content: `You are an expert educational content creator. Based on the YouTube video information, create comprehensive flashcards covering key formulas, definitions, and concepts.

Return a JSON object:
{
  "flashcards": [
    {
      "front": "Question or term (e.g., What is Newton's Second Law or Photosynthesis)",
      "back": "Answer or definition with explanation",
      "category": "formula or definition or concept",
      "importance": "high or medium or low"
    }
  ]
}

Create 15-25 flashcards covering the most important content from the video.`
        },
        {
          role: 'user',
          content: `Create comprehensive flashcards from this educational video:

${videoContent}

Focus on:
- Key formulas and equations
- Important definitions and terminology
- Core concepts and principles
- Critical facts students must remember`
        }
      ];

      try {
        const flashcardResponse = await makeOpenAIRequest(flashcardMessages, 2000);
        const flashcardsData = JSON.parse(flashcardResponse);
        roadmapData.flashcards = flashcardsData.flashcards;
      } catch (flashcardErr) {
        console.error('Error generating flashcards:', flashcardErr);
        // Continue without flashcards if there's an error
      }

      // Call the parent function to save this roadmap
      if (onGenerateRoadmap) {
        onGenerateRoadmap(roadmapData);
      }

      setLoading(false);
      onClose();
    } catch (err) {
      console.error('Error generating roadmap:', err);
      setError('Failed to generate learning roadmap. Please try again.');
      setLoading(false);
      setStep('preview');
    }
  };

  // Parse ISO 8601 duration to readable format
  const parseDuration = (duration) => {
    const match = duration.match(/PT(\d+H)?(\d+M)?(\d+S)?/);
    const hours = (match[1] || '').replace('H', '');
    const minutes = (match[2] || '').replace('M', '');
    const seconds = (match[3] || '').replace('S', '');

    let result = '';
    if (hours) result += `${hours}h `;
    if (minutes) result += `${minutes}m `;
    if (seconds) result += `${seconds}s`;
    return result.trim() || '< 1m';
  };

  // Format number (views count)
  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  return (
    <div className={`fixed inset-0 ${darkMode ? 'bg-gray-900/80' : 'bg-black/50'} backdrop-blur-sm z-50 flex items-center justify-center p-4`}>
      <div className={`relative w-full max-w-2xl rounded-2xl shadow-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-red-500 to-pink-500 flex items-center justify-center">
              <Youtube className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                YouTube to Learning Goal
              </h2>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Generate a structured course from any YouTube video
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-full hover:bg-gray-100 ${darkMode ? 'hover:bg-gray-700' : ''} transition-colors`}
          >
            <X className={`w-6 h-6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 'input' && (
            <div className="space-y-6">
              <div>
                <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  YouTube Video URL
                </label>
                <div className="flex space-x-3">
                  <input
                    type="text"
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className={`flex-1 px-4 py-3 rounded-lg border-2 ${
                      darkMode
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    } focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all`}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && videoUrl) {
                        fetchVideoInfo();
                      }
                    }}
                  />
                  <button
                    onClick={fetchVideoInfo}
                    disabled={!videoUrl || loading}
                    className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-semibold hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  >
                    {loading ? (
                      <Loader className="w-5 h-5 animate-spin" />
                    ) : (
                      'Analyze'
                    )}
                  </button>
                </div>
                <p className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Paste any YouTube video URL (e.g., youtube.com/watch?v=... or youtu.be/...)
                </p>
              </div>

              {error && (
                <div className="p-4 rounded-lg bg-red-50 border border-red-200 flex items-start space-x-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                  <div>
                    <p className="font-semibold text-red-800">Error</p>
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              )}

              <div className={`p-6 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} border ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                <h3 className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  How it works:
                </h3>
                <ol className={`space-y-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <li className="flex items-start space-x-2">
                    <span className="font-semibold text-purple-500">1.</span>
                    <span>Paste a YouTube video URL</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="font-semibold text-purple-500">2.</span>
                    <span>We analyze the video content, title, and description</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="font-semibold text-purple-500">3.</span>
                    <span>AI generates a structured learning roadmap with lessons</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="font-semibold text-purple-500">4.</span>
                    <span>Start learning with tasks, quizzes, and the original video as reference</span>
                  </li>
                </ol>
              </div>
            </div>
          )}

          {step === 'mode-select' && videoInfo && (
            <div className="space-y-6">
              {/* Video Preview */}
              <div className="flex items-start space-x-4">
                <img
                  src={videoInfo.thumbnail}
                  alt={videoInfo.title}
                  className="w-40 h-auto rounded-lg shadow-md"
                />
                <div className="flex-1">
                  <h3 className={`font-bold text-lg mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {videoInfo.title}
                  </h3>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {videoInfo.channelTitle}
                  </p>
                  <div className={`flex items-center gap-3 mt-2 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                    <span>{parseDuration(videoInfo.duration)}</span>
                    <span>•</span>
                    <span>{formatNumber(videoInfo.viewCount)} views</span>
                  </div>
                </div>
              </div>

              {/* Mode Selection */}
              <div>
                <h3 className={`font-bold text-lg mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Choose How to Learn:
                </h3>
                <div className="grid grid-cols-1 gap-4">
                  {/* Learning Roadmap Option */}
                  <button
                    onClick={() => {
                      setSelectedMode('roadmap');
                      generateLearningRoadmap();
                    }}
                    className={`p-6 rounded-xl border-2 text-left transition-all hover:scale-102 ${
                      darkMode
                        ? 'bg-gray-700 border-gray-600 hover:border-purple-500'
                        : 'bg-white border-gray-200 hover:border-purple-400'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-2xl">📚</span>
                      </div>
                      <div className="flex-1">
                        <h4 className={`font-bold text-lg mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          Learning Roadmap
                        </h4>
                        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          Generate a structured course with lessons, quizzes, and tasks based on the video content
                        </p>
                      </div>
                    </div>
                  </button>

                  {/* Study Companion Option */}
                  <button
                    onClick={() => {
                      setSelectedMode('companion');
                      if (onStudyCompanion) {
                        onStudyCompanion(videoInfo);
                      }
                      onClose();
                    }}
                    className={`p-6 rounded-xl border-2 text-left transition-all hover:scale-102 ${
                      darkMode
                        ? 'bg-gray-700 border-gray-600 hover:border-green-500'
                        : 'bg-white border-gray-200 hover:border-green-400'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-2xl">🤖</span>
                      </div>
                      <div className="flex-1">
                        <h4 className={`font-bold text-lg mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          Study Companion
                        </h4>
                        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          Watch the video with AI insights appearing in sync. Ask questions as you learn!
                        </p>
                      </div>
                    </div>
                  </button>
                </div>

                <button
                  onClick={() => setStep('input')}
                  className={`mt-4 text-sm ${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-800'}`}
                >
                  ← Try a different video
                </button>
              </div>
            </div>
          )}

          {step === 'preview' && videoInfo && (
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <img
                  src={videoInfo.thumbnail}
                  alt={videoInfo.title}
                  className="w-48 h-36 object-cover rounded-lg shadow-lg"
                />
                <div className="flex-1">
                  <h3 className={`font-bold text-lg mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    {videoInfo.title}
                  </h3>
                  <p className={`text-sm mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    by <span className="font-semibold">{videoInfo.channelTitle}</span>
                  </p>
                  <div className={`flex items-center space-x-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    <span>⏱️ {parseDuration(videoInfo.duration)}</span>
                    <span>👁️ {formatNumber(videoInfo.viewCount)} views</span>
                  </div>
                </div>
              </div>

              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} max-h-40 overflow-y-auto`}>
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {videoInfo.description.slice(0, 300)}
                  {videoInfo.description.length > 300 && '...'}
                </p>
              </div>

              {videoInfo.tags && videoInfo.tags.length > 0 && (
                <div>
                  <p className={`text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Topics:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {videoInfo.tags.slice(0, 8).map((tag, idx) => (
                      <span
                        key={idx}
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          darkMode
                            ? 'bg-purple-900 text-purple-200'
                            : 'bg-purple-100 text-purple-800'
                        }`}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setStep('input')}
                  className={`flex-1 px-6 py-3 rounded-lg font-semibold border-2 ${
                    darkMode
                      ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  } transition-colors`}
                >
                  Choose Different Video
                </button>
                <button
                  onClick={generateLearningRoadmap}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-semibold hover:scale-105 transition-transform flex items-center justify-center space-x-2"
                >
                  <CheckCircle className="w-5 h-5" />
                  <span>Generate Learning Path</span>
                </button>
              </div>
            </div>
          )}

          {step === 'generating' && (
            <div className="py-12 text-center">
              <Loader className={`w-16 h-16 mx-auto mb-4 animate-spin ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
              <h3 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                Generating Your Learning Roadmap...
              </h3>
              <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                AI is analyzing the video content and creating structured lessons
              </p>
              <div className={`mt-6 max-w-md mx-auto p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  This may take 10-20 seconds. We're creating personalized lessons, tasks, and quizzes based on the video content.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default YouTubeUpload;
